/**
 * PhoneInput — wraps intl-tel-input's official React component.
 *
 * - Default country: Saudi Arabia ("sa")
 * - Returns the full E.164 number (e.g. "+966512345678") via `onChangeNumber`
 * - Validates with libphonenumber utils once loaded
 * - Saudi placeholder example: 501234567
 */
import { useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import IntlTelInput, { type IntlTelInputRef } from 'intl-tel-input/react'
import 'intl-tel-input/styles'

export const SA_MOBILE_PLACEHOLDER = '501234567'

function customPlaceholder(placeholder: string, country: { iso2?: string }) {
  if (country.iso2 === 'sa') return SA_MOBILE_PLACEHOLDER
  return placeholder
}

interface PhoneInputProps {
  /** Current value – full E.164 number or local number */
  value?: string
  /** Called with the full international number on every change */
  onChangeNumber: (fullNumber: string) => void
  /** Called with validity boolean on every change */
  onChangeValidity?: (valid: boolean) => void
  /** HTML input attributes (id, required, className, style…) */
  inputProps?: Omit<React.ComponentPropsWithoutRef<'input'>, 'onInput' | 'placeholder'>
  /** Extra class(es) applied to the intl-tel-input container */
  containerClassName?: string
  /** Inline style applied to the intl-tel-input container */
  containerStyle?: React.CSSProperties
  /** Whether the input is disabled */
  disabled?: boolean
  /** Current locale – drives i18n for country names (ar | en) */
  locale?: string
}

export interface PhoneValidationResult {
  valid: boolean
  number: string
  empty: boolean
}

export interface PhoneInputHandle {
  getInstance: () => ReturnType<IntlTelInputRef['getInstance']>
  getInput: () => ReturnType<IntlTelInputRef['getInput']>
  validatePhone: (options?: { required?: boolean }) => Promise<PhoneValidationResult>
}

const PhoneInput = forwardRef<PhoneInputHandle, PhoneInputProps>(
  ({ value, onChangeNumber, onChangeValidity, inputProps, containerClassName, containerStyle, disabled, locale }, ref) => {
    const itiRef = useRef<IntlTelInputRef>(null)

    useImperativeHandle(ref, () => ({
      getInstance: () => itiRef.current?.getInstance() ?? null,
      getInput: () => itiRef.current?.getInput() ?? null,
      validatePhone: async (options = {}) => {
        const instance = itiRef.current?.getInstance()
        if (!instance) {
          return { valid: !options.required, number: '', empty: true }
        }

        await instance.promise

        const number = instance.getNumber() || ''
        const input = itiRef.current?.getInput()
        const hasTyped = Boolean(input?.value?.trim())

        if (!hasTyped && !number) {
          return { valid: !options.required, number: '', empty: true }
        }

        return {
          valid: instance.isValidNumber() === true,
          number,
          empty: false,
        }
      },
    }))

    const handleNumber = useCallback(
      (number: string) => onChangeNumber(number),
      [onChangeNumber],
    )

    const handleValidity = useCallback(
      (valid: boolean) => onChangeValidity?.(valid),
      [onChangeValidity],
    )

    const { placeholder: _placeholder, ...restInputProps } = inputProps || {}

    return (
      <IntlTelInput
        ref={itiRef}
        initialValue={value}
        onChangeNumber={handleNumber}
        onChangeValidity={handleValidity}
        onChangeCountry={() => {}}
        disabled={disabled}
        usePreciseValidation
        inputProps={{
          dir: 'ltr',
          ...restInputProps,
        }}
        style={containerStyle}
        initOptions={{
          initialCountry: 'sa',
          separateDialCode: true,
          countrySearch: true,
          formatAsYouType: true,
          formatOnDisplay: true,
          nationalMode: false,
          autoPlaceholder: 'aggressive',
          placeholderNumberType: 'MOBILE',
          customPlaceholder,
          containerClass: ['iti--fullwidth', containerClassName].filter(Boolean).join(' '),
          loadUtils: () => import('intl-tel-input/utils'),
          ...(locale === 'ar'
            ? { i18n: undefined }
            : {}),
        }}
      />
    )
  },
)

PhoneInput.displayName = 'PhoneInput'
export default PhoneInput
